import { useState, useEffect } from 'react';
import {
  CaretDownIcon,
  XIcon,
  SlidersIcon,
  ArrowsCounterClockwiseIcon,
  ChartLineIcon,
  ArrowRightIcon,
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Checkbox } from '../components/ui/checkbox';

const TestTypeSelector = ({
  selectedTypes = [],
  onSelect,
  categories = [],
  subjects = [],
  apiResponse = null,
  isMobile = false,
  onResetFilters, // New prop for reset functionality
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localSelected, setLocalSelected] = useState([]);
  const categoriesData = apiResponse?.data?.categories || categories;
  const subjectsData = apiResponse?.data?.subjects || subjects;

  const [selectedCategory, setSelectedCategory] = useState(null);

  // Initialize local state with selectedTypes from props
  useEffect(() => {
    setLocalSelected(selectedTypes);

    // Determine if all groups in a category are selected to set the radio button
    if (selectedTypes.length > 0) {
      for (const category of categoriesData) {
        if (category.groups) {
          const allGroupsSelected = category.groups.every((group) => selectedTypes.includes(group._id));
          if (allGroupsSelected) {
            setSelectedCategory(category.categoryTitle);
            break;
          }
        }
      }
    } else {
      setSelectedCategory(null);
    }
  }, [selectedTypes, categoriesData]);

  // Handle reset from parent component
  useEffect(() => {
    if (onResetFilters && selectedTypes.length === 0) {
      setLocalSelected([]);
      setSelectedCategory(null);
    } else {
    }
  }, [selectedTypes, onResetFilters]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleDrawer = () => setIsOpen(!isOpen);
  const closeDrawer = () => setIsOpen(false);

  const handleTypeToggle = (type, categoryTitle) => {
    const newSelected = localSelected.includes(type)
      ? localSelected.filter((t) => t !== type)
      : [...localSelected, type];
    setLocalSelected(newSelected);
  };

  const handleCategoryToggle = (categoryTitle) => {
    const category = categoriesData.find((cat) => cat.categoryTitle === categoryTitle);

    if (!category || !category.groups) return;

    // If selecting a different category
    if (selectedCategory !== categoryTitle) {
      // Clear any previously selected category and its groups
      const newSelected = localSelected.filter((type) => {
        // Keep only subjects
        return subjectsData.some((subject) => subject._id === type);
      });

      // Select all groups in the new category
      const categoryGroupIds = category.groups.map((group) => group._id);
      setLocalSelected([...newSelected, ...categoryGroupIds]);
      setSelectedCategory(categoryTitle);
    } else {
      // If clicking the same category, deselect it
      setSelectedCategory(null);
      const categoryGroupIds = category.groups.map((group) => group._id);
      setLocalSelected(localSelected.filter((type) => !categoryGroupIds.includes(type)));
    }
  };

  const handleReset = () => {
    setLocalSelected([]);
    setSelectedCategory(null);
    onSelect([]);
    closeDrawer();
  };

  const handleViewAnalysis = () => {
    onSelect(localSelected);
    closeDrawer();
  };

  const drawerVariants = {
    hidden: { x: '-100%', opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: '-100%', opacity: 0 },
  };

  const getSelectedDisplay = () => {
    if (localSelected.length === 0) {
      return 'All Quizrr Mock Test';
    }

    if (!Array.isArray(localSelected) || localSelected.length === 0) {
      return 'All Quizrr Mock Test';
    }

    const allGroups = categoriesData.flatMap((category) => category.groups || []);

    const selectedGroups = allGroups.filter((group) => localSelected.includes(group._id));

    const selectedSubjects = subjectsData.filter((subject) => localSelected.includes(subject._id));

    const allSelected = [...selectedGroups, ...selectedSubjects];

    if (allSelected.length > 0) {
      if (allSelected.length === 1) {
        return allSelected[0].title;
      } else if (allSelected.length === 2) {
        const result = allSelected.map((item) => item.title).join(', ');

        return result;
      } else {
        const result = `${allSelected[0].title} +${allSelected.length - 1} more`;

        return result;
      }
    }

    return 'All Quizrr Mock Test';
  };

  const getSelectedCountInCategory = (category) => {
    return category.groups?.filter((group) => localSelected.includes(group._id)).length || 0;
  };

  const hasSelection = localSelected.length > 0;

  return (
    <div className={`relative ${isMobile ? 'mb-2' : 'mb-6'}`}>
      {/* Trigger Button */}
      <button
        onClick={toggleDrawer}
        className={`flex items-center justify-between w-full 
          bg-[var(--q3-accent-light)] rounded-[360px] 
          border border-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-light)] 
          transition-colors cursor-pointer
          ${isMobile ? 'px-3 py-1 mt-1.5 text-sm' : 'px-4 py-2 text-base'}
        `}
      >
        <div className="flex items-center gap-2 text-left overflow-hidden">
          <div className="relative flex-shrink-0">
            <SlidersIcon size={16} className="text-[var(--q3-accent-normal)]" />
            {localSelected.length > 0 && (
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-[var(--q3-danger-normal)] rounded-full"></div>
            )}
          </div>
          <p
            className={`font-medium text-[var(--q3-accent-normal)] whitespace-nowrap overflow-hidden text-ellipsis ${
              isMobile ? 'max-w-[80px] text-sm' : 'max-w-none text-base'
            }`}
            title={getSelectedDisplay()}
          >
            {getSelectedDisplay()}
          </p>
        </div>
        <CaretDownIcon
          size={isMobile ? 16 : 20}
          className={`text-[var(--q3-accent-normal)] transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Drawer Overlay and Content */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-[rgba(16,24,40,0.8)] backdrop-blur-[4px] z-[10000] cursor-pointer"
              onClick={closeDrawer}
            />

            {/* Floating Close Button */}
            <button
              onClick={closeDrawer}
              className="fixed top-4 left-[430px] z-[10002] p-2 bg-[var(--q3-surface-default)] rounded-full shadow hover:bg-[var(--q3-surface-dim)] transition-colors cursor-pointer"
            >
              <XIcon size={20} className="text-[var(--q3-neutral-default)]" />
            </button>

            {/* Drawer */}
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={drawerVariants}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`fixed top-0 left-0 bottom-0 ${isMobile ? 'w-full' : 'w-[400px]'} bg-[var(--q3-surface-default)] shadow-2xl overflow-y-auto z-[10001]`}
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="border-b border-[var(--q3-surface-dim)] p-6 pb-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <SlidersIcon size={20} className="text-[var(--q3-neutral-default)]" />
                      <h3 className="text-lg font-bold text-[var(--q3-neutral-default)]">Test type</h3>
                    </div>
                    <button
                      onClick={handleReset}
                      className="flex items-center gap-1 text-sm font-medium text-[var(--q3-danger-normal)] hover:underline cursor-pointer"
                    >
                      <ArrowsCounterClockwiseIcon size={16} />
                      Reset
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-6 space-y-6 overflow-y-auto">
                  {/* Categories Section */}
                  {categoriesData && categoriesData.length > 0 && (
                    <div className="space-y-4">
                      {categoriesData.map((category) => {
                        const selectedCount = getSelectedCountInCategory(category);
                        const totalCount = category.groups?.length || 0;
                        const isCategorySelected = selectedCategory === category.categoryTitle;

                        return (
                          <div key={category.categoryTitle} className="space-y-2">
                            {/* Category Header */}
                            <div className="flex items-center justify-between">
                              <button
                                onClick={() => handleCategoryToggle(category.categoryTitle)}
                                className="flex items-center gap-3 w-full text-left"
                              >
                                <div
                                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
                                    ${
                                      isCategorySelected
                                        ? 'border-[var(--q3-accent-normal)] bg-white'
                                        : 'border-[var(--q3-neutral-dim)] hover:border-[var(--q3-neutral-default)]'
                                    }
                                  `}
                                >
                                  {isCategorySelected && (
                                    <div className="w-2.5 h-2.5 rounded-full bg-[var(--q3-accent-normal)]" />
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-1 p-2 rounded-lg hover:bg-[var(--q3-surface-dim)] transition-colors">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`text-sm font-semibold ${
                                        isCategorySelected
                                          ? 'text-[var(--q3-neutral-default)]'
                                          : 'text-[var(--q3-neutral-default)]'
                                      }`}
                                    >
                                      {category.categoryTitle}
                                    </span>
                                    <span className="text-xs text-[var(--q3-neutral-dim)] bg-[var(--q3-surface-dim)] px-2 py-1 rounded-full whitespace-nowrap">
                                      {totalCount} {totalCount === 1 ? 'test' : 'tests'}
                                    </span>
                                  </div>
                                  <CaretDownIcon
                                    size={16}
                                    className={`text-[var(--q3-neutral-dim)] transition-transform ${isCategorySelected ? 'rotate-180' : ''}`}
                                  />
                                </div>
                              </button>
                            </div>

                            {/* Category Groups - Only show when category is selected */}
                            {isCategorySelected && (
                              <div className="ml-6 space-y-2 border-l border-[var(--q3-surface-dim)] pl-4">
                                {category.groups?.map((group) => (
                                  <label
                                    key={group._id}
                                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-[var(--q3-surface-dim)] cursor-pointer transition-colors"
                                  >
                                    <Checkbox
                                      checked={localSelected.includes(group._id)}
                                      onCheckedChange={() => handleTypeToggle(group._id, category.categoryTitle)}
                                    />
                                    <span className="text-sm text-[var(--q3-neutral-default)] flex-1">
                                      {group.title}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Subjects Section */}
                  {/* {subjectsData && subjectsData.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-[var(--q3-neutral-default)] mb-4">
                        Subjects
                      </h4>
                      <div className="space-y-3">
                        {subjectsData.map(subject => (
                          <label
                            key={subject._id}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-[var(--q3-surface-dim)] cursor-pointer transition-colors"
                          >
                            <Checkbox
                              checked={localSelected.includes(subject._id)}
                              onCheckedChange={() => handleTypeToggle(subject._id)}
                            />
                            <div className="flex items-center gap-2 flex-1">
                              <span className="text-sm text-[var(--q3-neutral-default)]">
                                {subject.title}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )} */}
                </div>

                {/* Bottom Action Buttons */}
                <div className="border-t border-[var(--q3-surface-dim)] p-4 bg-[var(--q3-surface-default)]">
                  <div className="space-y-3">
                    <button
                      onClick={handleViewAnalysis}
                      className={`w-full rounded-lg font-medium transition-colors flex items-center justify-center gap-2 cursor-pointer ${
                        hasSelection
                          ? 'bg-[var(--q3-brand-default)] text-[var(--q3-surface-default)] hover:bg-[var(--q3-brand-dark)]'
                          : 'bg-[var(--q3-brand-default)] text-[var(--q3-surface-default)] hover:bg-[var(--q3-brand-dark)]'
                      }`}
                    >
                      <ChartLineIcon size={16} />
                      View Analysis
                    </button>

                    <button
                      onClick={handleViewAnalysis}
                      className="w-full py-2.5 bg-[var(--q3-accent-normal)] rounded-[360px] text-[var(--q3-surface-default)] font-medium hover:bg-[var(--q3-accent-normal)] transition-colors text-sm flex items-center justify-center gap-2 cursor-pointer"
                    >
                      View Analysis
                      <ArrowRightIcon size={16} className="text-[var(--q3-surface-default)]" weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TestTypeSelector;
